import React, { useState, useEffect } from 'react'
import { Card, Timeline, Typography, Spin, message, Tag, Space, Empty } from 'antd'
import {
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { getAuditTrail } from '../../../../backend-integration/api/auditTrail'

const { Text } = Typography

const HistoryContent = ({ accountData }) => {
  const [loading, setLoading] = useState(true)
  const [historyItems, setHistoryItems] = useState([])

  useEffect(() => {
    loadAccountHistory()
  }, [accountData])

  const loadAccountHistory = async () => {
    setLoading(true)

    try {
      // Try multiple filter approaches to find entries related to this account
      const filterOptions = [
        // Primary: Try metadata.billingId with account number (this is what's actually stored!)
        { 'metadata.billingId': accountData.billingId },
        // Direct account actions
        { entityType: 'FOLLOW_UP_ACCOUNT', entityId: accountData._id },
        // Try entityId directly for account actions
        { entityId: accountData._id },
        // Try with patient ID if available
        ...(accountData.patientId ? [{ 'metadata.patientId': accountData.patientId }] : []),
        // Try account number in metadata
        ...(accountData.accountNo ? [{ 'metadata.accountNo': accountData.accountNo }] : []),
        // Legacy metadata.accountId
        { 'metadata.accountId': accountData._id }
      ]

      let foundEntries = []

      for (let i = 0; i < filterOptions.length; i++) {
        const filters = {
          ...filterOptions[i],
          startDate: dayjs().subtract(1, 'year').toISOString(),
          endDate: dayjs().toISOString()
        }

        const result = await getAuditTrail(filters, 1, 100)

        if (result.data && result.data.length > 0) {
          foundEntries = result.data
          break
        }
      }

      // If no specific entries found, get all and filter client-side
      if (foundEntries.length === 0) {
        const allResult = await getAuditTrail(
          {
            startDate: dayjs().subtract(1, 'year').toISOString(),
            endDate: dayjs().toISOString()
          },
          1,
          500
        )

        // Filter client-side for entries related to this account
        foundEntries = (allResult.data || []).filter((entry) => {
          const relatedToAccount =
            // Primary: Check metadata.billingId (most important!)
            entry.metadata?.billingId === accountData.billingId ||
            // Direct account entity actions
            (entry.entityType === 'FOLLOW_UP_ACCOUNT' && entry.entityId === accountData._id) ||
            // Direct entityId match
            entry.entityId === accountData._id ||
            // Patient ID in metadata
            (accountData.patientId && entry.metadata?.patientId === accountData.patientId) ||
            // Account number match
            (accountData.accountNo && entry.metadata?.accountNo === accountData.accountNo) ||
            // Legacy accountId
            entry.metadata?.accountId === accountData._id

          if (relatedToAccount) {
            console.log('✅ Found related entry:', {
              action: entry.action,
              entityType: entry.entityType,
              description: entry.description,
              matchedBy:
                entry.metadata?.billingId === accountData._id
                  ? 'billingId'
                  : entry.entityType === 'FOLLOW_UP_ACCOUNT'
                  ? 'directAccount'
                  : entry.entityId === accountData._id
                  ? 'entityId'
                  : entry.metadata?.patientId === accountData.patientId
                  ? 'patientId'
                  : entry.metadata?.accountNo === accountData.accountNo
                  ? 'accountNo'
                  : 'accountId'
            })
          }

          return relatedToAccount
        })

        console.log(`Found ${foundEntries.length} entries after client-side filtering`)
      }

      // Convert audit trail entries to timeline format
      const historyItems = foundEntries
        .map((entry) => ({
          id: entry._id,
          date: entry.timestamp,
          type: entry.action.toLowerCase(),
          description: entry.description,
          user: entry.userName,
          entityType: entry.entityType,
          success: entry.success,
          source: 'audit',
          metadata: entry.metadata
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date))

      setHistoryItems(historyItems)

      if (historyItems.length === 0) {
        message.info(
          `No audit trail entries found for account ${
            accountData.accountNo || accountData._id
          }. Check console for debugging details.`
        )
      }
    } catch (error) {
      console.error('Error loading audit trail:', error)

      if (error.message?.includes('Active Connection Route cannot be found')) {
        message.warning(
          'Audit trail connector not configured. Please set up the audit_trail connector in Agilite to view account history.'
        )
      } else {
        message.error('Failed to load account history: ' + error.message)
      }

      // Show empty state instead of fallback data
      setHistoryItems([])
    } finally {
      setLoading(false)
    }
  }

  const getTimelineColor = (type, success = true) => {
    if (!success) return 'red'

    switch (type) {
      case 'payment':
      case 'create':
        return 'green'
      case 'update':
      case 'allocate':
        return 'blue'
      case 'delete':
      case 'void':
        return 'red'
      case 'phone':
      case 'email':
        return 'orange'
      default:
        return 'blue'
    }
  }

  const getTimelineIcon = (type, entityType) => {
    switch (type) {
      case 'payment':
      case 'allocate':
        return <DollarOutlined />
      case 'create':
        if (entityType === 'INVOICE') return <FileTextOutlined />
        return <UserOutlined />
      case 'update':
        return <EditOutlined />
      case 'delete':
        return <DeleteOutlined />
      case 'phone':
        return <PhoneOutlined />
      case 'email':
        return <MailOutlined />
      default:
        return null
    }
  }

  const formatDescription = (item) => {
    // If description is too generic, try to make it more specific
    if (item.description === `${item.type.toUpperCase()} ${item.entityType}` && item.metadata) {
      switch (item.type) {
        case 'create':
          if (item.entityType === 'INVOICE') {
            return `Invoice created for ${item.metadata.amount ? `R${item.metadata.amount}` : 'unknown amount'}`
          }
          break
        case 'payment':
          return `Payment of ${item.metadata.amount ? `R${item.metadata.amount}` : 'unknown amount'} received`
        case 'update':
          if (item.metadata.changes && item.metadata.changes.length > 0) {
            const fieldNames = item.metadata.changes.map((c) => c.field).join(', ')
            return `Updated: ${fieldNames}`
          }
          break
      }
    }

    return item.description
  }

  if (loading) {
    return (
      <Card size='small' title='Account History'>
        <Spin tip='Loading account history...' />
      </Card>
    )
  }

  if (historyItems.length === 0) {
    return (
      <Card size='small' title='Account History'>
        <Empty
          description={
            <div>
              <p>No audit trail history found for this account.</p>
              <p style={{ fontSize: '12px', color: '#666' }}>Check browser console for debugging information.</p>
            </div>
          }
        />
      </Card>
    )
  }

  return (
    <div>
      <Card size='small' title={`Account History (${historyItems.length} entries)`}>
        <Timeline>
          {historyItems.map((item, index) => (
            <Timeline.Item
              key={item.id || index}
              color={getTimelineColor(item.type, item.success)}
              dot={getTimelineIcon(item.type, item.entityType)}
            >
              <div>
                <Space direction='vertical' size={4} style={{ width: '100%' }}>
                  <div>
                    <Text strong>{dayjs(item.date).format('DD MMM YYYY HH:mm')}</Text>
                    <Space style={{ marginLeft: 8 }}>
                      <Tag color={getTimelineColor(item.type, item.success)} size='small'>
                        {item.type.toUpperCase()}
                      </Tag>
                      {item.entityType && <Tag size='small'>{item.entityType}</Tag>}
                      {item.source && (
                        <Tag color='default' size='small'>
                          {item.source}
                        </Tag>
                      )}
                    </Space>
                  </div>

                  <Text>{formatDescription(item)}</Text>

                  <Text type='secondary' style={{ fontSize: '12px' }}>
                    By: {item.user || 'System'}
                  </Text>

                  {!item.success && (
                    <Text type='danger' style={{ fontSize: '12px' }}>
                      Failed: {item.metadata?.errorMessage || 'Unknown error'}
                    </Text>
                  )}
                </Space>
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </Card>
    </div>
  )
}

export default HistoryContent
